import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import type { Account, NavigationUser } from '@app/src/context/AccountContext'
import { useAccount } from '@app/src/context/AccountContext'
import { useAmplitude } from '@app/src/context/AmplitudeContext'
import { useLocale } from '@app/src/context/LocaleContext'
import { useMenuState } from '@app/src/hooks/mui-hooks'
import useLanguageSelection from '@app/src/hooks/useLanguageSelection'
import { AmplitudeTrackingEvents, ResourceTypes, Solutions } from '@app/src/wf-constants'
import { ArrowDropDown } from '@mui/icons-material'
import { Button, ListItemIcon, Menu, MenuItem } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React from 'react'
import ReactCountryFlag from 'react-country-flag'

const OnboardingLanguageSelector: React.FC = () => {
  const { account } = useAccount()
  const { availableLanguageOptions, hideLanguageSelector } = useLanguageSelection()
  const [isMenuOpen, menuAnchorElement, openMenu, closeMenu] = useMenuState()
  const user = account?.user
  const { locale, setLocale } = useLocale()
  const selectedLanguage = locale ? availableLanguageOptions.find(l => l.localeName === locale) : undefined
  const otherLanguages = availableLanguageOptions.filter(l => l.localeName !== locale)
  const { trackEvent } = useAmplitude()
  const { mutate } = useUpdateResource<Partial<NavigationUser>>()
  const queryClient = useQueryClient()

  const handleUserSettingsSubmit = async (language: string): Promise<void | JSX.Element> => {
    const editedUser = { ...(user || {}), culture: language }

    mutate(
      { url: endpoints.saveResource(Solutions.Resources, ResourceTypes.User), body: editedUser },
      {
        onSuccess: () => {
          queryClient.setQueryData<Account>([FetchKey.Account], acc => {
            if (!acc?.user) return acc as Account
            return {
              ...acc,
              user: { ...acc.user, culture: language },
            }
          })
        },
      },
    )

    if (editedUser?.culture !== locale) {
      trackEvent({
        name: AmplitudeTrackingEvents.Onboarding.UserInfo.LanguageChanged,
        eventProps: {
          page: 'Onboarding',
          language: editedUser?.culture,
        },
      })
    }
  }

  const handleChangeLanguage = async (language: string) => {
    setLocale(language)
    closeMenu()
    await handleUserSettingsSubmit(language)
  }

  if (hideLanguageSelector) {
    return null
  }

  return (
    <>
      <Button
        startIcon={<ReactCountryFlag countryCode={selectedLanguage?.flagCode} svg style={{ fontSize: '2em' }} />}
        onClick={openMenu}
      >
        {selectedLanguage?.shortLabel}
        <ArrowDropDown />
      </Button>
      <Menu
        anchorEl={menuAnchorElement}
        open={isMenuOpen}
        onClose={closeMenu}
        sx={({ zIndex }) => ({
          zIndex: zIndex.modal + 2,
        })}
      >
        {otherLanguages.map(language => (
          <MenuItem key={language.localeName} onClick={() => handleChangeLanguage(language.localeName)}>
            <ListItemIcon>
              <ReactCountryFlag countryCode={language.flagCode} svg style={{ fontSize: '2em' }} />
            </ListItemIcon>
            {language.shortLabel}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default OnboardingLanguageSelector
